// ── Word-by-word stream reveal ─────────────────────────────────────────
//
// Wraps the words a stream has *just* appended in `<span>`s so each one
// fades into view with a short staggered delay — matching the polished
// feel of the major frontier-model UIs.
//
// Callers run this from a layout effect so the wrap happens before paint
// (no flicker between the plain markdown text and the wrapped spans).
// `<pre>` / `<code>` are deliberately NOT skipped: streamed shell
// heredocs and code samples should fade in alongside the prose.
//
// Only the newly-appended tail is wrapped. Words that were already on
// screen before this pass are left as untouched text nodes — a bare text
// node renders fully opaque, which is exactly what a settled word should
// look like, so there is nothing to gain from giving it a span.
//
// That distinction is the whole performance story here. This used to wrap
// *every* word on every pass, giving the settled ones an
// `animation-delay` far in the past to hold them visible. Because the
// markdown renderer rebuilds the subtree from its source string on each
// change, that meant re-creating one element per word of the entire
// message on every single delta — quadratic in response length, and
// multiplied by an animated `filter` that put each of those elements on
// its own compositing layer. Wrapping only the tail makes a pass cost
// proportional to what actually changed.
//
// Counting still visits every text node, but counting is string work
// with no DOM allocation and no layout implication, which is orders of
// magnitude cheaper than the element creation it now avoids.

export const STREAM_WORD_STAGGER_MS = 22;

/** Ceiling on how many words a single pass will animate.
 *
 *  Steady-state streaming appends a handful of words per frame and never
 *  approaches this. It exists for the batch cases — a mid-turn
 *  rehydration, or a burst that coalesced several deltas into one frame
 *  — where animating the entire batch would both cost a lot of spans at
 *  once and, at `STREAM_WORD_STAGGER_MS` apiece, queue up a reveal far
 *  longer than the burst that produced it. Words past the ceiling render
 *  immediately at full opacity. */
export const STREAM_MAX_ANIMATED_WORDS = 60;

const IS_WHITESPACE_RE = /^\s+$/;

interface StreamTextNodeInfo {
  node: Text;
  /** `text.split(/(\s+)/)` — words and whitespace runs, in order. */
  tokens: string[];
  /** Count of non-whitespace tokens in `tokens`. */
  words: number;
  /** Already inside a word span from an earlier pass. */
  preWrapped: boolean;
}

/** Wrap the words appended since `settledBefore` in animated spans.
 *
 *  Returns the subtree's total word count, which the caller keeps as the
 *  next pass's `settledBefore`. */
export const wrapNewWordsForStreamReveal = (
  root: HTMLElement,
  settledBefore: number,
  wordClassName: string
): number => {
  // Pass 1 — tokenize and count, touching nothing. We need the total
  // word count before we can know which words are new.
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const infos: StreamTextNodeInfo[] = [];
  let total = 0;
  let n: Node | null;
  while ((n = walker.nextNode())) {
    const node = n as Text;
    const text = node.textContent ?? "";
    if (!text) continue;
    // Split keeping whitespace runs intact so we don't lose spacing.
    const tokens = text.split(/(\s+)/);
    let words = 0;
    for (const tok of tokens) {
      if (tok !== "" && !IS_WHITESPACE_RE.test(tok)) words++;
    }
    if (words === 0) continue;
    infos.push({
      node,
      tokens,
      words,
      preWrapped:
        node.parentElement?.classList.contains(wordClassName) ?? false,
    });
    total += words;
  }

  // Words at or after this index animate; everything before it is
  // settled and stays as plain text.
  const firstAnimated = Math.max(
    settledBefore,
    total - STREAM_MAX_ANIMATED_WORDS
  );
  if (firstAnimated >= total) return total;

  // Pass 2 — wrap only the tail.
  let wordIdx = 0;
  for (const info of infos) {
    const nodeEnd = wordIdx + info.words;
    // Entirely settled, or already wrapped by an earlier pass (rare —
    // the subtree is normally rebuilt between passes). Either way,
    // leave the node exactly as it is.
    if (nodeEnd <= firstAnimated || info.preWrapped) {
      wordIdx = nodeEnd;
      continue;
    }
    // Two things make wrapping safe inside `<pre>`:
    //   * we only wrap *non-whitespace* tokens — the surrounding
    //     whitespace runs (including indentation and newlines) are
    //     re-attached as plain text nodes so the `<pre>`-preserved
    //     formatting is exactly what the highlighter produced; and
    //   * the word class is `display: inline` so wrapping a keyword
    //     inside `<span class="hljs-keyword">` keeps the highlighter's
    //     color cascading through unchanged.
    const frag = document.createDocumentFragment();
    let modified = false;
    for (const tok of info.tokens) {
      if (tok === "") continue;
      if (IS_WHITESPACE_RE.test(tok)) {
        frag.appendChild(document.createTextNode(tok));
        continue;
      }
      if (wordIdx >= firstAnimated) {
        const span = document.createElement("span");
        span.className = wordClassName;
        span.textContent = tok;
        span.style.animationDelay = `${
          (wordIdx - firstAnimated) * STREAM_WORD_STAGGER_MS
        }ms`;
        frag.appendChild(span);
        modified = true;
      } else {
        // A settled word sharing a text node with new ones. Plain text,
        // so it renders opaque with no animation attached.
        frag.appendChild(document.createTextNode(tok));
      }
      wordIdx++;
    }
    if (modified) {
      info.node.parentNode?.replaceChild(frag, info.node);
    }
  }
  return total;
};
